// File: frontend/src/hooks/usePreviousContestData.ts
// REVISED: The data structure now includes a map for individual player scores for the user's lineup.

import { useCallback } from 'react';

// Define the new, more comprehensive structure of the data we will store
export interface PreviousContestData {
  myTotalScore: number;
  myRank: number;
  // This map stores the previous score for EVERY lineup in the contest for the leaderboard
  lineupStats: Record<string, { score: number; rank: number }>;
  // --- NEW FIELD ---
  // This map stores the previous score for each INDIVIDUAL player in the user's own lineup
  playerScores: Record<string, number>; // Key: player_id, Value: score
}

/**
 * A custom hook to manage storing and retrieving the previous state of contest data
 * from sessionStorage. This allows animations to work even after a page refresh.
 */
export const usePreviousContestData = (contestId: number | null) => {
  const key = `contest_${contestId}_prev_data_v3`; // Use a new key to avoid conflicts with old format

  const get = useCallback((): PreviousContestData | null => {
    if (!contestId) return null;
    try {
      const stored = sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Failed to parse previous contest data from sessionStorage", error);
      return null;
    }
  }, [key, contestId]);

  const set = useCallback((values: PreviousContestData) => {
    if (!contestId) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(values));
    } catch (error) {
      console.error("Failed to save previous contest data to sessionStorage", error);
    }
  }, [key, contestId]);

  return { get, set };
};